import { pb, escapeFilterValue } from '../lib/pocketbase'
import { throwApiError } from '../utils/apiErrorHandler'
import { logger } from '../utils/logger'
import type { StoredPlan } from '../lib/plans'

// Subscription status values from PocketBase schema
export type SubscriptionStatus = 'trial' | 'active' | 'past due' | 'cancelled' | 'free'
// Tier vocabulary lives in lib/plans — legacy values are still accepted on
// read, so stored records need no migration.
export type SubscriptionTier = StoredPlan

export type Agency = {
  id: string
  name: string | null // Maps to agency_name in PocketBase
  logo_url: string | null
  primary_color: string | null
  secondary_color: string | null
  owner_id: string | null
  owner_name?: string
  owner_email?: string
  user_count?: number
  // Subscription fields
  subscription_status?: SubscriptionStatus | null
  subscription_tier?: SubscriptionTier | null
  trial_ends_at?: string | null
  discount_code_used?: string | null
  is_lifetime_free?: boolean
  seat_limit?: number | null
  additional_seats?: number | null
  // Set when the agency is scheduled for removal. The nightly purge job acts on
  // it once the date passes; clearing it calls the removal off.
  deletion_scheduled_at?: string | null
  deletion_requested_by?: string | null
  // Team sign-in: email domains whose Google/Microsoft users auto-join.
  // Written only through api/sso-domains.js; the row rule rejects it from here.
  sso_domains?: string[] | null
  created: string
  updated: string
}

export type AgencyPayload = {
  name: string // Will be saved as agency_name
  logo_url?: string | null
  primary_color?: string | null
  secondary_color?: string | null
  owner_id?: string | null
  subscription_status?: SubscriptionStatus | null
  subscription_tier?: SubscriptionTier | null
  trial_ends_at?: string | null
  is_lifetime_free?: boolean
  seat_limit?: number | null
  additional_seats?: number | null
}

/**
 * Fetch a single agency by id. Used by the app shell to show the agency name.
 * Throws on failure — callers decide what to render, we don't invent a name.
 */
export async function getAgency(agencyId: string): Promise<Agency> {
  const record = await pb.collection('agencies').getOne<Record<string, any>>(agencyId)
  // The column is agency_name; the type says name. Map it, as listAllAgencies
  // does, so callers reading .name get the agency and not undefined.
  return { ...record, name: record.agency_name } as unknown as Agency
}

/**
 * List all agencies (for superuser or current user's agency)
 */
export async function listAllAgencies(): Promise<Agency[]> {
  try {
    const user = pb.authStore.model
    if (!user) {
      throw new Error('You must be signed in')
    }

    const isSuperAdmin = (user as any).role === 'superadmin'
    const userAgencyId = (user as any).agency_id

    // Super admin can see all, regular users see only their agency
    const filter = isSuperAdmin ? '' : `id = "${escapeFilterValue(userAgencyId)}"`
    
    const data = await pb.collection('agencies').getFullList<Record<string, any>>({
      sort: '+agency_name',
      filter,
      expand: 'owner_id'
    })

    return data.map(record => ({
      id: record.id,
      // PocketBase uses agency_name field
      name: record.agency_name || record.name || null,
      logo_url: record.logo_url ?? null,
      primary_color: record.primary_color ?? null,
      secondary_color: record.secondary_color ?? null,
      owner_id: record.owner_id ?? null,
      owner_name: (record.expand as any)?.owner_id?.name,
      owner_email: (record.expand as any)?.owner_id?.email,
      subscription_status: record.subscription_status ?? null,
      subscription_tier: record.subscription_tier ?? null,
      trial_ends_at: record.trial_ends_at ?? null,
      discount_code_used: record.discount_code_used ?? null,
      is_lifetime_free: record.is_lifetime_free ?? false,
      seat_limit: record.seat_limit ?? null,
      additional_seats: record.additional_seats ?? null,
      deletion_scheduled_at: record.deletion_scheduled_at || null,
      deletion_requested_by: record.deletion_requested_by || null,
      created: record.created ?? '',
      updated: record.updated ?? '',
    }))
  } catch (err: any) {
    throwApiError(err, 'Failed to load agencies')
  }
}

/**
 * Create a new agency (super admin only)
 */
/**
 * Create an agency, through the server.
 *
 * This used to `pb.collection('agencies').create()` from the browser with a
 * client-side superadmin check in front of it. `agencies.createRule` is now
 * `null` — nobody creates an agency from a client — because the same page also
 * set the caller's own `role` and `agency_id`, which is the self-escalation the
 * row rules exist to stop.
 *
 * The endpoint does the superadmin check server-side, where the caller cannot
 * reach it, and writes with admin credentials.
 */
export async function createAgency(payload: AgencyPayload): Promise<Agency> {
  const res = await fetch('/api/create-agency', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${pb.authStore.token}`,
    },
    body: JSON.stringify({
      agency_name: payload.name?.trim(),
      subscription_tier: payload.subscription_tier || 'independent',
      subscription_status: payload.subscription_status || 'trial',
      owner_email: (payload as { owner_email?: string }).owner_email,
    }),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    // The endpoint says WHY — not a superadmin, name already taken, that owner
    // is already in an agency. Passing it through beats a generic failure.
    throw new Error(data.error || `Could not create the agency (${res.status}).`)
  }
  return {
    id: data.id,
    name: data.agency_name,
    subscription_status: data.subscription_status,
    subscription_tier: data.subscription_tier,
  } as Agency
}

export async function updateAgency(agencyId: string, payload: Partial<AgencyPayload>): Promise<Agency> {
  try {
    const user = pb.authStore.model
    if (!user) {
      throw new Error('You must be signed in')
    }

    const isSuperAdmin = (user as any).role === 'superadmin'
    const userAgencyId = (user as any).agency_id

    if (!isSuperAdmin && agencyId !== userAgencyId) {
      throw new Error('You can only update your own agency')
    }

    // Build update payload - only include fields that are provided
    const updatePayload: Record<string, any> = {}
    if (payload.name !== undefined) updatePayload.agency_name = payload.name.trim()
    if (payload.logo_url !== undefined) updatePayload.logo_url = payload.logo_url
    if (payload.primary_color !== undefined) updatePayload.primary_color = payload.primary_color
    if (payload.secondary_color !== undefined) updatePayload.secondary_color = payload.secondary_color
    if (payload.owner_id !== undefined) updatePayload.owner_id = payload.owner_id
    if (payload.subscription_status !== undefined) updatePayload.subscription_status = payload.subscription_status
    if (payload.subscription_tier !== undefined) updatePayload.subscription_tier = payload.subscription_tier
    if (payload.trial_ends_at !== undefined) updatePayload.trial_ends_at = payload.trial_ends_at
    if (payload.is_lifetime_free !== undefined) updatePayload.is_lifetime_free = payload.is_lifetime_free
    if (payload.seat_limit !== undefined) updatePayload.seat_limit = payload.seat_limit
    if (payload.additional_seats !== undefined) updatePayload.additional_seats = payload.additional_seats

    const record = await pb.collection('agencies').update(agencyId, updatePayload)

    return {
      id: record.id,
      name: record.agency_name || record.name || null,
      logo_url: record.logo_url ?? null,
      primary_color: record.primary_color ?? null,
      secondary_color: record.secondary_color ?? null,
      owner_id: record.owner_id ?? null,
      subscription_status: record.subscription_status ?? null,
      subscription_tier: record.subscription_tier ?? null,
      trial_ends_at: record.trial_ends_at ?? null,
      is_lifetime_free: record.is_lifetime_free ?? false,
      seat_limit: record.seat_limit ?? null,
      additional_seats: record.additional_seats ?? null,
      deletion_scheduled_at: record.deletion_scheduled_at || null,
      deletion_requested_by: record.deletion_requested_by || null,
      created: record.created ?? '',
      updated: record.updated ?? '',
    }
  } catch (err: any) {
    throwApiError(err, 'Failed to update agency')
  }
}

/**
 * Schedule an agency for removal, or call it off.
 *
 * This used to be `deleteAgency`, calling `pb.collection('agencies').delete()`
 * — which `agencies.deleteRule` (null, superuser only) refused every single
 * time. The button had never worked.
 *
 * It is a scheduled purge rather than an immediate one because
 * `deletion_scheduled_at` and scripts/purge-deleted-agencies.js already exist,
 * already walk all 67 agency-scoped collections in dependency order, and
 * already give thirty days in which the decision can be reversed.
 */
async function postAgencyDeletion(agencyId: string, action: 'schedule' | 'cancel') {
  const res = await fetch('/api/agency-deletion', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${pb.authStore.token}`,
    },
    body: JSON.stringify({ agency_id: agencyId, action }),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    // The endpoint says WHY — "already scheduled for deletion on ...",
    // "only a superadmin can". That beats a generic failure.
    throw new Error(data.error || `Could not change the deletion schedule (${res.status}).`)
  }
  return data as {
    id: string
    deletion_scheduled_at: string | null
    deletion_requested_by: string | null
    note: string
  }
}

export const scheduleAgencyDeletion = (agencyId: string) => postAgencyDeletion(agencyId, 'schedule')
export const cancelAgencyDeletion = (agencyId: string) => postAgencyDeletion(agencyId, 'cancel')

/**
 * Get users for an agency
 */
export async function getAgencyUsers(agencyId: string): Promise<any[]> {
  try {
    const user = pb.authStore.model
    if (!user) {
      throw new Error('You must be signed in')
    }

    const isSuperAdmin = (user as any).role === 'superadmin'
    const userAgencyId = (user as any).agency_id

    if (!isSuperAdmin && agencyId !== userAgencyId) {
      throw new Error('You can only view users in your own agency')
    }

    // Search for users by agency_id (the actual field name in the schema)
    const users = await pb.collection('users').getFullList({
      filter: `agency_id = "${escapeFilterValue(agencyId)}"`,
      sort: '+name'
    })

    return users
  } catch (err: any) {
    throwApiError(err, 'Failed to get agency users')
  }
}


// ── Team sign-in ────────────────────────────────────────────────────────

/** Error from a team sign-in endpoint; `code` lets the caller branch. */
export class TeamSignInError extends Error {
  code: string | null
  status: number
  constructor(message: string, code: string | null, status: number) {
    super(message)
    this.name = 'TeamSignInError'
    this.code = code
    this.status = status
  }
}

async function postTeamSignIn<T>(path: string, body: unknown, fallback: string): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${pb.authStore.token}`,
    },
    body: JSON.stringify(body),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new TeamSignInError(data.error || fallback, data.code ?? null, res.status)
  }
  return data as T
}

/**
 * Attach the signed-in user to the agency that lists their email domain.
 * Throws TeamSignInError with code NO_MATCHING_DOMAIN, NOT_OAUTH_VERIFIED,
 * SEATS_FULL, AGENCY_INACTIVE, ALREADY_MEMBER or DOMAIN_AMBIGUOUS.
 */
export function joinByDomain(): Promise<{ agencyId: string; agencyName: string | null; role: 'agent' }> {
  return postTeamSignIn('/api/join-by-domain', {}, 'Could not join by domain')
}

/** Add or remove a team sign-in domain on the caller's agency. Admins only. */
export function updateSsoDomain(action: 'add' | 'remove', domain: string): Promise<{ sso_domains: string[] }> {
  return postTeamSignIn('/api/sso-domains', { action, domain }, 'Could not update team sign-in domains')
}
